/**
 * Route storage service — CRUD for Route entities in the graph model.
 */

import { db } from '../database/schema.js'
import type { Route } from '../schemas/route.js'
import { EntityStorageService } from './entity-storage.js'
import { validateRoute, runValidation } from './validation.js'
import type { StorageResult } from './base-storage.js'
import type { Origin } from '../schemas/origin.js'
import { edgeService, nodeRef } from '../services/edge-service.js'
import { featureStorage } from './feature-storage.js'

export interface CreateRouteData {
  name: string
  description?: string | undefined
  difficulty?: Route['difficulty'] | undefined
  origin: Origin
  tags?: string[] | undefined
  isPublic?: boolean | undefined
  createdFrom?: { type: 'plan' | 'activity'; id: string } | undefined
  createdBy: string
}

export interface PromoteOptions {
  name: string
  description?: string | undefined
  difficulty?: Route['difficulty'] | undefined
  tags?: string[] | undefined
  featureIds: string[]
  createdBy: string
}

class RouteStorageService extends EntityStorageService<Route> {
  protected readonly nodeType = 'route'

  constructor() {
    super(db.routes, 'route')
  }

  async promoteFromPlan(planId: string, options: PromoteOptions): Promise<StorageResult<Route>> {
    const sourceFeatures = await featureStorage.getFeaturesForEntity('plan', planId)
    const selected = sourceFeatures.filter(f => options.featureIds.includes(f.id))

    const result = await this.createRoute({
      name: options.name,
      description: options.description,
      difficulty: options.difficulty,
      tags: options.tags,
      origin: { type: 'user-created' },
      createdFrom: { type: 'plan', id: planId },
      createdBy: options.createdBy,
    })
    if (!result.success || !result.data) return result

    const routeId = result.data.id
    const rootGroupId = await this.getRootLayerGroupId(routeId)
    if (!rootGroupId) return result

    for (const feature of selected) {
      const {
        id: _id,
        createdAt: _ca,
        updatedAt: _ua,
        layerId: _li,
        tripIds: _ti,
        ...rest
      } = feature
      await featureStorage.createFeature(
        { ...rest, createdBy: options.createdBy },
        { entityType: 'route', entityId: routeId, groupId: rootGroupId }
      )
    }

    return result
  }

  async createRoute(data: CreateRouteData): Promise<StorageResult<Route>> {
    const routeData = {
      name: data.name,
      description: data.description,
      difficulty: data.difficulty,
      origin: data.origin,
      tags: data.tags ?? [],
      isPublic: data.isPublic ?? false,
      createdBy: data.createdBy,
    }

    const validation = runValidation(validateRoute, routeData, { context: 'create' })
    if (!validation.isValid) {
      return this.handleValidationError(validation)
    }

    return this.createWithLayerGroup(routeData as Omit<Route, 'id' | 'createdAt' | 'updatedAt'>)
  }

  async updateRoute(id: string, updates: Partial<Route>): Promise<StorageResult<Route>> {
    const validation = runValidation(validateRoute, updates, { context: 'update' })
    if (!validation.isValid) {
      return this.handleValidationError(validation)
    }
    return this.update(id, updates)
  }

  async deleteRoute(id: string): Promise<StorageResult<void>> {
    return this.deleteWithEdges(id)
  }

  async getRoutesForUser(_userId: string): Promise<StorageResult<Route[]>> {
    return this.executeOperation(async () => {
      const all = await this.table.toArray()
      return all.filter(r => (r as Record<string, unknown>).accessStatus !== 'revoked')
    }, 'get routes for user')
  }

  /** Activities that followed this route. */
  async getActivityIdsFollowingRoute(routeId: string): Promise<string[]> {
    return edgeService.getSubjectIds(nodeRef('route', routeId), 'follows-route')
  }
}

export const routeStorage = new RouteStorageService()
